import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppSource } from "./AppSource";
import { AppIndexReader } from "./AppIndex";
import { AppIcons } from "./AppIcons";
import { AppSearch } from "./AppSearch";

/**
 * Exact chat/message lookup for search results and validated AI evidence. Plain text only.
 *
 * ⭐ ON THE DESIGN SYSTEM (audit 2026-10-12). The window was a bare `<dialog>` with the browser's own "Close" button and
 * one paragraph per message reading "2026-10-12T07:05:00.000Z · Contact" - the wire's own spelling of a time, in UTC.
 * Now it is one of the app's dialogs (`wac-keydlg`'s family: tokens, the same backdrop), named by its own heading,
 * closed by the family's icon button (Esc and the backdrop work too), and each message says who wrote it and when on
 * the reader's own clock (`AppSearch.WhenOf`). The message that was asked for is marked for the eye AND for a screen
 * reader (`aria-current`).
 * ⛔ Every word is written with `textContent` - a message is the contact's text and never markup.
 * ⛔ FATE 1: an answer that cannot be read is a sentence in the window, never a report.
 */
export class AppMessageContext {
    public static readonly Title: string = "Message in its conversation";
    public static readonly Reading: string = "Reading the messages around it…";
    public static readonly LostWhy: string = "The messages could not be read just now. Close this window and open the result again.";
    public static readonly GoneWhy: string = "This message is no longer in your archive.";
    private static Made: number = 0;

    private Source: AppSource;
    private Dialog: HTMLDialogElement | null = null;
    /** The mouse went down on the dimmed page (outside the window's box) - see `OnBackdrop`. */
    private DownOutside: boolean = false;
    public constructor(Source: AppSource) { this.Source = Source; }

    public Open = (ChatId: string, MessageId: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Dialog !== null) { this.Dialog.remove(); }
            const Dialog: HTMLDialogElement = document.createElement("dialog");
            this.Dialog = Dialog;
            AppMessageContext.Made = AppMessageContext.Made + 1;
            const HeadId: string = "wac-mctitle-" + String(AppMessageContext.Made);
            Dialog.className = "message-context";
            Dialog.setAttribute("aria-labelledby", HeadId);
            const Head: HTMLElement = document.createElement("div"); Head.className = "mchead";
            const Heading: HTMLElement = document.createElement("h2"); Heading.id = HeadId; Heading.textContent = AppMessageContext.Title;
            const Close: HTMLButtonElement = document.createElement("button"); Close.type = "button";
            Close.className = "btn ghost icon sm"; Close.setAttribute("aria-label", "Close"); Close.title = "Close";
            const Icon: unknown = AppIcons.Build(document, AppIcons.Close, 16);
            if (Icon !== null) { Close.appendChild(Icon as Node); }
            Close.onclick = this.Close;
            Head.append(Heading, Close);
            const Body: HTMLElement = document.createElement("div"); Body.className = "message-context-body";
            Body.setAttribute("aria-live", "polite"); Body.textContent = AppMessageContext.Reading;
            Body.classList.add("wac-search-busy");
            Dialog.append(Head, Body); document.body.appendChild(Dialog);
            Dialog.addEventListener("close", this.OnClosed);
            Dialog.addEventListener("mousedown", this.OnDown);
            Dialog.addEventListener("click", this.OnBackdrop);
            Dialog.showModal();
            const Self: AppMessageContext = this;
            function Answer(Text: string): void { Self.Loaded(Dialog, Body, Text, MessageId); }
            this.Source.QueryMessages({ action: "context", chatId: ChatId, messageId: MessageId, beforeCount: 3, afterCount: 3 }, Answer);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.Open"); }
    };

    private Loaded = (Dialog: HTMLDialogElement, Body: HTMLElement, Text: string, Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Dialog !== Dialog || Dialog.isConnected === false) { return; }
            Body.classList.remove("wac-search-busy"); Body.textContent = "";
            const Data: Record<string, unknown> | null = AppIndexReader.ObjectIn(Text);
            if (Data === null || Data.ok !== true || Array.isArray(Data.messages) === false) {
                Body.textContent = AppMessageContext.LostWhy; return;
            }
            for (const Raw of Data.messages as Record<string, unknown>[]) {
                if (Raw === null || typeof Raw !== "object" || typeof Raw.text !== "string") { continue; }
                Body.appendChild(AppMessageContext.Row(Raw, Raw.messageId === Id));
            }
            if (Body.children.length === 0) { Body.textContent = AppMessageContext.GoneWhy; }
            const Target: Element | null = Body.querySelector(".message-context-target");
            if (Target !== null && typeof (Target as HTMLElement).scrollIntoView === "function") { (Target as HTMLElement).scrollIntoView({ block: "nearest" }); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.Loaded"); }
    };

    /** "You · 12 Oct 2026, 14:05" (or "Them", or the name alone when the time cannot be read). Pure. */
    public static WhoLine = (FromMe: unknown, At: unknown): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Who: string = "Them";
            if (FromMe === true) { Who = "You"; }
            const When: string = AppSearch.WhenOf(At);
            if (When.length === 0) { return Who; }
            return Who + " · " + When;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.WhoLine");
            return "";
        }
    };

    /** One message: who and when on the first line, the words under it. ⛔ `textContent` only. */
    private static Row = (Raw: Record<string, unknown>, IsTarget: boolean): HTMLElement => {
        const Row: HTMLElement = document.createElement("div");
        if (getCoreUtils().KErrorMode === true) { return Row; }
        try {
            Row.className = "mcrow them";
            if (Raw.fromMe === true) { Row.className = "mcrow me"; }
            if (IsTarget === true) { Row.className = Row.className + " message-context-target"; Row.setAttribute("aria-current", "true"); }
            const Who: HTMLElement = document.createElement("span"); Who.className = "mcwho";
            Who.textContent = AppMessageContext.WhoLine(Raw.fromMe, Raw.at);
            const Words: HTMLElement = document.createElement("span"); Words.className = "mctext";
            Words.textContent = nz(Raw.text as string);
            Row.append(Who, Words);
            return Row;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.Row");
            return Row;
        }
    };

    /**
     * Is the point OUTSIDE the window's own box? Pure (`OnBackdrop`'s whole decision).
     * ⚠ WHY A BOX AND NOT "the click's target is the dialog" (verifier, 2026-10-12): the dialog has padding, and its
     * padding IS the dialog - a click in the 20px inside its edge closed the window; so did selecting a message's words
     * with the mouse and letting go beside them (the click then lands on the two elements' common ancestor: the dialog).
     */
    public static Outside = (X: number, Y: number, Left: number, Top: number, Right: number, Bottom: number): boolean => {
        // DOCUMENTED EXCEPTION: pure arithmetic. ERROR-SUPPRESSED-JUSTIFIED: comparisons cannot throw.
        return X < Left || X > Right || Y < Top || Y > Bottom;
    };

    /** A press that BEGAN on the dimmed page - only such a press may close the window when it is let go there too. */
    private OnDown = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.DownOutside = false;
            if (this.Dialog === null || Ev.target !== this.Dialog) { return; }
            const Box: DOMRect = this.Dialog.getBoundingClientRect();
            this.DownOutside = AppMessageContext.Outside(Ev.clientX, Ev.clientY, Box.left, Box.top, Box.right, Box.bottom);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.OnDown"); }
    };

    /** A click on the dimmed page around the window closes it - pressed AND released outside the window's box. */
    private OnBackdrop = (Ev: MouseEvent): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Began: boolean = this.DownOutside;
            this.DownOutside = false;
            if (this.Dialog === null || Ev.target !== this.Dialog || Began === false) { return; }
            const Box: DOMRect = this.Dialog.getBoundingClientRect();
            if (AppMessageContext.Outside(Ev.clientX, Ev.clientY, Box.left, Box.top, Box.right, Box.bottom) === true) { this.Close(); }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.OnBackdrop"); }
    };

    /** Closed by Esc, by the button or by the backdrop: the window is taken out of the page. */
    private OnClosed = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { if (this.Dialog !== null) { this.Dialog.remove(); this.Dialog = null; } }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.OnClosed"); }
    };

    private Close = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { if (this.Dialog !== null) { this.Dialog.close(); } }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppMessageContext.Close"); }
    };
}
